import { apiClient } from 'api/apiClient';
import {
  InventoryFilterSearchParams,
  InventoryFilterSearchParamsSchema,
  InventoryIndexAPIResponse,
  InventoryRecentActivityRequest,
  InventoryAccountFilterSearchParams,
  InventoryAccountIndexAPIResponse,
  InventoryAccountFilterSearchParamsSchema,
  ActionedInvoicesAPIResponse,
  ActionedInvoicesFilterSearchParams,
  ActionedInvoicesFilterSearchParamsSchema,
  UploadWidgetFilterSearchParams,
  UploadWidgetFilterSearchParamsSchema,
  CancelDeleteInvoiceResponse,
  DownloadInvoiceResponse,
} from './schema';
import { BasePagination, prepareSortingParams } from 'api/common-schemas';

export type NonGraniteItem = {
  id: string;
  filename: string;
  filetype: string;
  processing_status: 'processing' | 'pending action';
  progress: number;
  created_at: string;
  upload_status: 'Success' | 'Failed';
};

const mapOrderColumns = {
  parent_name_order: 'ParentAccountName',
  parent_number_order: 'ParentAccount',
  account_name_order: 'AccountName',
  account_number_order: 'Account',
  address_1_order: 'Address1',
  service_type_order: 'ServiceType',
  vendor_order: 'Vendor',
  status_order: 'OrderStatus',
  completion_date_order: 'CompletionDate',
  end_date_order: 'EndDate',
  btn_order: 'BTN',
  serial_number_order: 'SerialNumber',
  mac_order: 'MAC',
  model_number_order: 'ModelName',
  ip_order: 'WAN_IP_Address',
  tag_number_order: 'TagNumber',
  product_offering_order: 'ProductOffering',
};
const accountMapOrderColumns = {
  parent_name_order: 'ParentAccountName',
  parent_number_order: 'ParentAccount',
  account_number_order: 'Account',
  account_name_order: 'AccountName',
  address_order: 'Address1',
  city_order: 'City',
  state_order: 'State',
  zip_order: 'ZIP',
  services_count_order: 'ServiceTypeCount',
  billing_type_order: 'BillingType',
};

export const getInventoryIndex = async (
  params: InventoryFilterSearchParams,
): Promise<InventoryIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    params,
    mapOrderColumns,
    InventoryFilterSearchParamsSchema,
  );
  const response = await apiClient.get('/api/v1/inventory/services', {
    params: newQueryParams,
  });

  return {
    data: response.data?.results,
    metadata: {
      ...response.data?.info,
      total_items: response.data?.info?.total_records,
    },
  };
};

export const getInventoryStaticData = async () => {
  const response = await apiClient.get('/api/v1/inventory/static');

  return response.data;
};

export const getInventoryServicesChanges = async (dateRange: string) => {
  const response = await apiClient.get('/api/v1/inventory/services/changes', {
    params: {
      date_range: dateRange,
    },
  });

  return response.data;
};

export const getInventoryServicesCounts = async () => {
  const response = await apiClient.get('/api/v1/inventory/services/counts');

  return response.data;
};

export const getInventoryRecentActivity = async (
  params: InventoryRecentActivityRequest,
) => {
  const response = await apiClient.get('/api/v1/inventory/activity', {
    params: params,
  });

  return response.data?.results;
};

export const getInventoryExport = async (
  params: InventoryFilterSearchParams,
): Promise<InventoryIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    params,
    mapOrderColumns,
    InventoryFilterSearchParamsSchema,
  );
  const response = await apiClient.get('/api/v1/inventory/services/export', {
    params: newQueryParams,
  });

  return {
    data: response.data?.results,
    metadata: {
      ...response.data?.info,
      total_items: response.data?.info?.total_records,
    },
  };
};

// accounts
export const getInventoryAccountIndex = async (
  params: InventoryAccountFilterSearchParams,
): Promise<InventoryAccountIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    params,
    accountMapOrderColumns,
    InventoryAccountFilterSearchParamsSchema,
  );
  const response = await apiClient.get('/api/v1/inventory/accounts', {
    params: newQueryParams,
  });

  return {
    data: response.data?.results,
    metadata: {
      ...response.data?.info,
      total_items: response.data?.info?.total_records,
    },
  };
};

export const getInventoryAccountExport = async (
  params: InventoryAccountFilterSearchParams,
): Promise<InventoryAccountIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    params,
    accountMapOrderColumns,
    InventoryAccountFilterSearchParamsSchema,
  );
  const response = await apiClient.get('/api/v1/inventory/accounts/export', {
    params: newQueryParams,
  });

  return {
    data: response.data?.results,
    metadata: {
      ...response.data?.info,
      total_items: response.data?.info?.total_records,
    },
  };
};

const mapUploadWidgetOrderColumns = {
  filename_order: 'filename',
  filetype_order: 'filetype',
  processing_status_order: 'processing_status',
  progress_order: 'progress',
  created_at_order: 'created_at',
  upload_status_order: 'upload_status',
};

export const getUploadWidgetIndex = async (
  params: UploadWidgetFilterSearchParams,
): Promise<BasePagination<NonGraniteItem>> => {
  const newQueryParams = prepareSortingParams(
    params,
    mapUploadWidgetOrderColumns,
    UploadWidgetFilterSearchParamsSchema,
  );
  const response = await apiClient.get(`/api/v1/orders/invoices`, {
    params: {
      ...newQueryParams,
      sort_order: (newQueryParams.sort_order || 'DESC').toLowerCase(),
    },
  });

  return {
    data: response.data?.data,
    metadata: {
      page: response.data?.page,
      page_size: response.data?.page_size,
      total_items: response.data?.total_items,
      total_pages: response.data?.total_pages,
    },
  };
};

const mapActionedInvoicesOrderColumns = {
  service_id_order: 'service_id',
  invoice_type_order: 'invoice_type',
  address1_order: 'address1',
  address2_order: 'address2',
  city_order: 'city',
  state_order: 'state',
  zip_code_order: 'zip_code',
  upload_date_order: 'upload_date',
  actioned_date_order: 'actioned_date',
  service_type_order: 'service_type',
  invoice_filename_order: 'invoice_filename',
  service_mrc_order: 'service_mrc',
  bandwidth_order: 'bandwidth',
  vendor_order: 'vendor',
  vendor_account_number_order: 'vendor_account_number_order',
  parent_name_order: 'parent_name',
  parent_number_order: 'parent_number',
};

export const getActionedInvoicesIndex = async (
  params: ActionedInvoicesFilterSearchParams,
): Promise<ActionedInvoicesAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    params,
    mapActionedInvoicesOrderColumns,
    ActionedInvoicesFilterSearchParamsSchema,
  );
  const response = await apiClient.get('/api/v1/orders/invoices/services', {
    params: newQueryParams,
  });

  return {
    data: response.data?.data,
    metadata: {
      ...response.data,
    },
  };
};

export const deleteInvoice = async (
  invoiceId: string,
): Promise<CancelDeleteInvoiceResponse> => {
  const response = await apiClient.delete(
    `/api/v1/orders/invoices/${invoiceId}/delete`,
  );

  return response.data;
};

export const cancelInvoice = async (
  id: string,
): Promise<CancelDeleteInvoiceResponse> => {
  const response = await apiClient.patch(
    `/api/v1/orders/invoices/${id}/cancel`,
    {
      invoice_id: id,
      upload_id: id,
    },
  );

  return response.data;
};

export const getInvoiceDownload = async (
  invoiceId: string,
): Promise<DownloadInvoiceResponse> => {
  const response = await apiClient.get(
    `/api/v1/orders/invoices/${invoiceId}/download`,
  );

  return response.data;
};

export const downloadInvoiceFile = async (invoiceId: string): Promise<Blob> => {
  const response = await apiClient.get(
    `/api/v1/orders/invoices/${invoiceId}/download`,
    { responseType: 'blob' },
  );

  const blob = new Blob([response.data], {
    type: response.headers['content-type'] || 'application/pdf',
  });

  return blob;
};
